import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Pagination,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Card,
  CardBody,
  Chip,
  Divider,
} from "@nextui-org/react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faArrowsAltH,
  faTimes,
  faInfoCircle,
  faCopy,
  faTrash,
  faColumns,
  faChartBar,
} from "@fortawesome/free-solid-svg-icons";
import { capitalize, StringLimit } from "../../controllers/APIController";
import {
  DeleteOnedata,
  getDataWithFilterUrl,
} from "../../controllers/APIController";

import { SearchInput } from "../../utils/helper";
import LoadingPage from "../../components/Loader/LoadingPage";
import BreadCrumbsPage from "../../components/BreadCrumbsPage";
import NullData from "../../components/ErrorPage/NullData";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const MetricsBuilderCards = () => {
  const navigate = useNavigate();
  const { _orgId } = useParams();
  // const [isLoading, setisLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const [metrics, setMetrics] = useState([]);
  const [reRender, setReRender] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("");
  const [categories, setCategories] = useState([]);
  const [layoutMode, setLayoutMode] = useState("grid");

  // Preview & Arrange state
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [arrangedMetrics, setArrangedMetrics] = useState([]);

  const UserLogged = JSON.parse(sessionStorage.getItem("userData"));
  const userRole = UserLogged && UserLogged?.user?.userRole;

  const OrgInfo = UserLogged && UserLogged.user.LastOrgId;
  const orgFilterUrl = "filters[Organization][id][$eqi]=" + OrgInfo;

  const handleNavigate = () => {
    navigate(`/${_orgId}/metrics-builder/create`);
  };

  const deleteFormHandler = async (id) => {
    try {
      await DeleteOnedata("metrics", id);
      setReRender((pre) => !pre);
    } catch (error) {
      console.log(error);
    }
  };

  const createDuplicateHandler = (id) => {
    navigate(`create?id=${id}`);
  };

  // Preview & Arrange functions
  const openPreviewModal = () => {
    setArrangedMetrics([...metrics]);
    setIsPreviewModalOpen(true);
  };

  const closePreviewModal = () => {
    setIsPreviewModalOpen(false);
  };

  // const { data: metricData, isLoading } = useQuery({
  //   queryKey: [`metrics-${currentPage}`],
  //   queryFn: () =>
  //     getDataWithFilterUrl("metrics", orgFilterUrl, currentPage, 20),
  // });

  const { data: metricData, isLoading } = useQuery({
    queryKey: ["metrics", currentPage],
    queryFn: () =>
      getDataWithFilterUrl("metrics", orgFilterUrl, currentPage, 20),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (!OrgInfo) {
      return;
    }

    const list = metricData?.data || [];

    if (list.length > 0) {
      list.sort((a, b) => {
        if (
          a?.attributes?.Name.trim().toLowerCase() <
          b?.attributes?.Name.trim().toLowerCase()
        ) {
          return -1;
        }
        if (
          a?.attributes?.Name.trim().toLowerCase() >
          b?.attributes?.Name.trim().toLowerCase()
        ) {
          return 1;
        }
        return 0;
      });
    }
    setMetrics(list);

    // Extract unique categories from metrics data
    const uniqueCategories = [
      ...new Set(
        list.map((item) => item?.attributes?.Category).filter(Boolean)
      ),
    ];
    setCategories(uniqueCategories);
  }, [metricData, orgFilterUrl]);

  const handleNavigateToMetric = (id) => {
    navigate(`/${_orgId}/metrics-builder/${id}`);
  };

  // Handle filter selection changes
  const handleFilterSelection = (key) => {
    setSelectedFilter(key);
  };

  // Function to assign colors to categories
  const getCategoryColor = (category) => {
    const categoryColors = {
      "reference data": "primary",
      environment: "success",
      social: "warning",
      governance: "danger",
      "vendor details": "warning",
      energy: "success",
      waste: "warning",
      water: "primary",
      emissions: "danger",
      biodiversity: "success",
      community: "warning",
      labor: "primary",
      "human rights": "danger",
      ethics: "secondary",
      compliance: "success",
      "risk management": "warning",
      transparency: "primary",
    };

    return categoryColors[category.toLowerCase()] || "default";
  };

  // Filter metrics based on the search term and selected filter
  const filteredMetrics = metrics.filter((item) => {
    const name = item?.attributes?.Name || "";
    const description = item?.attributes?.Description || "";
    const category = item?.attributes?.Category || "";
    const matchesSearchTerm =
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter =
      selectedFilter === "" ||
      category.toLowerCase() === selectedFilter.toLowerCase();
    const isNotSurvey = category.toLowerCase() !== "survey";
    return matchesSearchTerm && matchesFilter && isNotSurvey;
  });

  const ItemPerPage = 20;
  // const totalPage = Math.ceil(filteredMetrics.length / ItemPerPage);
  const totalPage = metricData?.meta?.pagination?.pageCount || 1;


  return (
    <div className="min-h-screen bg-slate-50">
      {/* Page Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-6 shadow-sm">
        <div className="flex items-center justify-between max-w-full">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-900">
              Metrics Builder
            </h1>
            <p className="text-base text-neutral-600 mt-2">
              Create and manage ESG metrics for your organization
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              color="primary"
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 font-medium"
              size="sm"
              onClick={handleNavigate}
            >
              Create new metric
            </Button>
            <Button
              color="primary"
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 font-medium"
              size="sm"
              onClick={openPreviewModal}
            >
              Preview & Arrange
            </Button>
          </div>
        </div>
      </div>

      <BreadCrumbsPage>
        <div className="w-full px-2 py-2">
          {/* Metric Repository Section */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-6 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
              <h2 className="text-xl font-semibold text-slate-900 mb-2">
                Metric Repository
              </h2>
              <p className="text-sm text-slate-600">
                Browse and manage your organization's ESG metrics. Use search
                and filters to find specific metrics quickly.
              </p>
            </div>

            <div className="px-4 py-6">
              <div className="flex items-center justify-between mb-6">
                <SearchInput
                  searchText={searchTerm}
                  setSearchText={setSearchTerm}
                  placeholderText={"Search for any ESG metric"}
                />

                <div className="flex items-center gap-4 ml-4">
                  {/* Layout Mode Selector */}
                  <div className="flex items-center gap-2">
                    {/* <span className="text-sm font-medium text-slate-700">Layout Mode:</span> */}
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant={layoutMode === "grid" ? "solid" : "bordered"}
                        color={layoutMode === "grid" ? "primary" : "default"}
                        onPress={() => setLayoutMode("grid")}
                        startContent={<FontAwesomeIcon icon={faColumns} />}
                        className={
                          layoutMode === "grid"
                            ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                            : ""
                        }
                      >
                        Grid Layout
                      </Button>
                      <Button
                        size="sm"
                        variant={layoutMode === "list" ? "solid" : "bordered"}
                        color={layoutMode === "list" ? "primary" : "default"}
                        onPress={() => setLayoutMode("list")}
                        startContent={<FontAwesomeIcon icon={faChartBar} />}
                        className={
                          layoutMode === "list"
                            ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                            : ""
                        }
                      >
                        List Layout
                      </Button>
                    </div>
                  </div>

                  <Dropdown>
                    <DropdownTrigger>
                      <Button
                        size="sm"
                        endContent={<FontAwesomeIcon icon={faFilter} />}
                        variant="bordered"
                        className="text-slate-600 hover:text-slate-900 px-6 border-slate-300"
                      >
                        {selectedFilter?.length > 0
                          ? capitalize(selectedFilter)
                          : "Filter by Category"}
                      </Button>
                    </DropdownTrigger>
                    <DropdownMenu
                      aria-label="Dropdown Variants"
                      onAction={handleFilterSelection}
                      selectedKeys={new Set([selectedFilter])}
                      selectionMode="single"
                    >
                      <DropdownItem key="">Clear Filters</DropdownItem>
                      {categories.map((category, index) => (
                        <DropdownItem
                          key={category.toLowerCase()}
                          color={getCategoryColor(category)}
                          variant="flat"
                        >
                          {category}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </div>

              <LoadingPage
                isLoading={isLoading}
                loadingText="Loading metrics..."
              >
                <div className="mt-6">
                  {filteredMetrics.length === 0 ? (
                    <div className="py-16 text-center">
                      <NullData
                        title={`No metric found. Click on "create" to make a new metric`}
                      />
                    </div>
                  ) : (
                    <div
                      className={`w-full ${
                        layoutMode === "grid"
                          ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                          : "space-y-6"
                      }`}
                    >
                      {filteredMetrics.map((item, index) => {
                        // const lowerPageLimit = (currentPage - 1) * ItemPerPage;
                        // const upperPageLimit = currentPage * ItemPerPage;
                        // if (index < lowerPageLimit || index >= upperPageLimit)
                        //   return null;

                        return (
                          <Card
                            key={item.id}
                            className={`min-w-[100%] border border-slate-200 hover:border-emerald-300 transition-colors duration-200 cursor-pointer shadow-none ${
                              layoutMode === "list" ? "flex-row" : ""
                            }`}
                            isPressable
                            onPress={() => handleNavigateToMetric(item.id)}
                          >
                            <CardBody
                              className={`${
                                layoutMode === "list" ? "p-3" : "p-4"
                              }`}
                            >
                              {layoutMode === "list" ? (
                                // List View Layout - Compact Horizontal
                                <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                                  {/* Left Section - Number and Category */}
                                  <div className="flex items-center gap-3 flex-shrink-0">
                                    <Chip
                                      size="md"
                                      color="success"
                                      variant="flat"
                                      className="bg-emerald-100 text-emerald-700 text-sm font-medium"
                                    >
                                      #{index + 1}
                                    </Chip>
                                    <Chip
                                      size="md"
                                      color={getCategoryColor(
                                        item?.attributes?.Category
                                      )}
                                      variant="flat"
                                      className="text-sm font-medium"
                                    >
                                      {item?.attributes?.Category ||
                                        "Reference Data"}
                                    </Chip>
                                  </div>

                                  {/* Center Section - Title and Description */}
                                  <div className="flex-1 min-w-0 lg:px-4">
                                    <h3 className="font-semibold text-slate-900 text-lg mb-2">
                                      {item?.attributes?.Name ||
                                        "Unnamed Metric"}
                                    </h3>
                                    <p className="text-slate-600 leading-relaxed mb-3">
                                      {item?.attributes?.Description ||
                                        "No description available"}
                                    </p>

                                    {/* Metadata Row */}
                                    <div className="flex items-center gap-4 text-sm text-slate-500">
                                      <span className="flex items-center gap-1">
                                        <span className="font-medium">
                                          Recommended Frequency:
                                        </span>
                                        <span>
                                          {item?.attributes?.Frequency ||
                                            "Not set"}
                                        </span>
                                      </span>
                                      <span className="hidden lg:inline text-slate-300">
                                        •
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <span className="font-medium">ID:</span>
                                        <span className="font-mono">
                                          {item.id}
                                        </span>
                                      </span>
                                    </div>
                                  </div>

                                  {/* Right Section - Action Buttons */}
                                  <div className="flex items-center gap-3 flex-shrink-0 lg:pl-4">
                                    <Button
                                      size="sm"
                                      variant="bordered"
                                      startContent={
                                        <FontAwesomeIcon icon={faCopy} />
                                      }
                                      onPress={() => {
                                        createDuplicateHandler(item.id);
                                      }}
                                      className="text-emerald-700 border-emerald-300 hover:border-emerald-400 hover:bg-emerald-50"
                                    >
                                      Duplicate
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="bordered"
                                      startContent={
                                        <FontAwesomeIcon icon={faTrash} />
                                      }
                                      onPress={() => {
                                        deleteFormHandler(item.id);
                                      }}
                                      className="text-red-700 border-red-300 hover:border-red-400 hover:bg-red-50"
                                      isDisabled={userRole !== "admin"}
                                    >
                                      Delete
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                // Grid View Layout - Vertical
                                <>
                                  <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                      <Chip
                                        size="sm"
                                        color="success"
                                        variant="flat"
                                        className="bg-emerald-100 text-emerald-700"
                                      >
                                        #{index}
                                      </Chip>
                                      <Chip
                                        size="sm"
                                        color={getCategoryColor(
                                          item?.attributes?.Category
                                        )}
                                        variant="flat"
                                      >
                                        {item?.attributes?.Category ||
                                          "Reference Data"}
                                      </Chip>
                                    </div>
                                    <FontAwesomeIcon
                                      icon={faInfoCircle}
                                      className="text-slate-400 text-sm cursor-pointer hover:text-slate-600"
                                    />
                                  </div>

                                  <h3 className="font-semibold text-slate-900 mb-2 text-base">
                                    {item?.attributes?.Name || "Unnamed Metric"}
                                  </h3>

                                  <p className="text-sm text-slate-600 mb-3 leading-relaxed">
                                    {item?.attributes?.Description ||
                                      "No description available"}
                                  </p>

                                  <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
                                    <span>
                                      Recommended Frequency:{" "}
                                      {item?.attributes?.Frequency || "Not set"}
                                    </span>
                                    <span>ID: {item.id}</span>
                                  </div>

                                  {/* Action Buttons */}
                                  <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                                    <Button
                                      size="sm"
                                      variant="bordered"
                                      startContent={
                                        <FontAwesomeIcon icon={faCopy} />
                                      }
                                      onPress={() => {
                                        createDuplicateHandler(item.id);
                                      }}
                                      className="text-emerald-700 border-emerald-300 hover:border-emerald-400 flex-1"
                                    >
                                      Duplicate
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="bordered"
                                      startContent={
                                        <FontAwesomeIcon icon={faTrash} />
                                      }
                                      onPress={() => {
                                        deleteFormHandler(item.id);
                                      }}
                                      className="text-red-700 border-red-300 hover:border-red-400"
                                      isDisabled={userRole !== "admin"}
                                    >
                                      Delete
                                    </Button>
                                  </div>
                                </>
                              )}
                            </CardBody>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>

                {filteredMetrics.length > 0 && (
                  <div className="w-full mt-10 flex items-center justify-center">
                    <Pagination
                      showControls
                      total={totalPage}
                      color="secondary"
                      page={currentPage}
                      onChange={setCurrentPage}
                      size="sm"
                    />
                  </div>
                )}
              </LoadingPage>
            </div>
          </div>
        </div>
      </BreadCrumbsPage>

      {/* Preview & Arrange Modal */}
      <Modal
        isOpen={isPreviewModalOpen}
        onOpenChange={setIsPreviewModalOpen}
        size="full"
        scrollBehavior="inside"
        classNames={{
          base: "max-w-[95vw] max-h-[95vh] m-4",
          body: "p-0",
        }}
      >
        <ModalContent>
          <ModalHeader className="flex items-center gap-3 bg-gradient-to-r from-emerald-50 to-white border-b border-emerald-200">
            <FontAwesomeIcon
              icon={faArrowsAltH}
              className="text-emerald-600 text-lg"
            />
            <div>
              <h2 className="text-xl font-semibold text-gray-800">
                Metrics Structure
              </h2>
              <p className="text-sm text-gray-600">
                View metrics statistics and category breakdown
              </p>
            </div>
          </ModalHeader>
          <ModalBody className="p-0">
            <div className="p-6">
              {/* Metrics Structure Content */}
              <div className="mt-6">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Metrics Overview
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card className="bg-white shadow-none">
                      <CardBody className="p-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-emerald-600">
                            {arrangedMetrics.length}
                          </div>
                          <div className="text-sm text-gray-600">
                            Total Metrics
                          </div>
                        </div>
                      </CardBody>
                    </Card>

                    <Card className="bg-white shadow-none">
                      <CardBody className="p-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-emerald-600">
                            {
                              [
                                ...new Set(
                                  arrangedMetrics
                                    .map((m) => m?.attributes?.Category)
                                    .filter(Boolean)
                                ),
                              ].length
                            }
                          </div>
                          <div className="text-sm text-gray-600">
                            Categories
                          </div>
                        </div>
                      </CardBody>
                    </Card>

                    <Card className="bg-white shadow-none">
                      <CardBody className="p-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-600">
                            {
                              arrangedMetrics.filter(
                                (m) => m?.attributes?.Frequency
                              ).length
                            }
                          </div>
                          <div className="text-sm text-gray-600">
                            With Recommended Frequency
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </div>

                  <Divider className="my-6" />

                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-800">
                      Category Breakdown
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(
                        arrangedMetrics.reduce((acc, metric) => {
                          const category =
                            metric?.attributes?.Category || "Uncategorized";
                          acc[category] = (acc[category] || 0) + 1;
                          return acc;
                        }, {})
                      ).map(([category, count]) => (
                        <div
                          key={category}
                          className="flex items-center justify-between p-3 bg-white rounded-lg border"
                        >
                          <span className="font-medium text-gray-700">
                            {category}
                          </span>
                          <Chip
                            size="sm"
                            color={getCategoryColor(category)}
                            variant="flat"
                          >
                            {count} metrics
                          </Chip>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter className="border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between w-full">
              <div className="text-sm text-gray-600">
                <span>View metrics statistics and category breakdown</span>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="bordered"
                  onPress={closePreviewModal}
                  className="px-6"
                >
                  Close
                </Button>
                {/* Removed Save Arrangement button as per edit hint */}
              </div>
            </div>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default MetricsBuilderCards;
